import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement, AddressElement, EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import {
  calcSetupFeeCents,
  calcEscrowDepositCents,
  tierLabelFor,
  effectivePerUserCents,
  pricingBreakdown,
} from '../lib/pricing';

const StripeCheckoutForm = ({
  onSuccess, onCancel, amountCents, breakdown, capacity, duration, tier, useCase, speed,
  checkoutMode = 'elements', stripePromise, clientSecret, onComplete, userEmail,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);

  const handleSubmit = async (event) => {
    console.log("StripeCheckoutForm handleSubmit triggered", event);
    event.preventDefault();
    setErrorMessage(null);

    if (!stripe || !elements || !isElementReady) {
      setErrorMessage('Payment form is still loading. Please wait a moment and try again.');
      return;
    }

    setIsProcessing(true);

    try {
      // Trigger form validation and data collection for Stripe Elements
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        setIsProcessing(false);
        return;
      }

      // OWASP A01: strip query/hash from current URL before handing it to Stripe
      // as return_url, so attacker-controlled URL params can't be reflected on return.
      const safeReturnUrl = `${window.location.origin}${window.location.pathname}`;

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: safeReturnUrl,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed.');
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
        return;
      }

      setErrorMessage('Unexpected status: ' + paymentIntent?.status);
    } catch (submitError) {
      setErrorMessage(submitError?.message || 'Unexpected payment processing error.');
    } finally {
      setIsProcessing(false);
    }
  };

  const cap = parseInt(capacity) || 1;
  const durSeconds = parseInt(duration) || 36000;

  // Graduated/marginal pricing — see src/lib/pricing.js for the canonical math.
  const setupFeeCentsLocal = calcSetupFeeCents(cap);
  const setupFeeDollars = setupFeeCentsLocal / 100;
  const localTierLabel = tierLabelFor(cap);
  const localEffectiveCents = effectivePerUserCents(cap);
  const localBands = pricingBreakdown(cap);

  // Escrow deposit — uses the same function as the Lambda, so the local fallback
  // estimate is always consistent with what Stripe will charge.
  const escrowDepositCentsLocal = calcEscrowDepositCents(
    cap, useCase || 'Office', tier || 'Basic', speed || 'Fast', durSeconds
  );
  const escrowDepositDollars = escrowDepositCentsLocal / 100;

  // Total Due must equal the sum of the two displayed line items
  // (Pre-calculated Usage Deposit + Service Cost). Fall back to amountCents
  // from the backend payment intent only if the local calculation is unavailable.
  const computedTotalDollars = escrowDepositDollars + setupFeeDollars;
  const localTotalDollars = (Number.isFinite(computedTotalDollars) && computedTotalDollars > 0)
    ? computedTotalDollars
    : ((amountCents || 0) / 100);

  // Authoritative server-side breakdown takes precedence over local computation
  // to guarantee the displayed total matches what Stripe will actually charge.
  const hasServerBreakdown = breakdown
    && Number.isFinite(breakdown.setupFeeCents)
    && Number.isFinite(breakdown.escrowDepositCents);
  const displaySetupDollars = hasServerBreakdown ? (breakdown.setupFeeCents / 100) : setupFeeDollars;
  const displayEscrowDollars = hasServerBreakdown ? (breakdown.escrowDepositCents / 100) : escrowDepositDollars;
  const displayCap = hasServerBreakdown && breakdown.capacity ? breakdown.capacity : cap;
  const displayTierLabel = (hasServerBreakdown && breakdown.tierLabel) ? breakdown.tierLabel : localTierLabel;
  const displayEffectivePerUserDollars = (
    hasServerBreakdown && Number.isFinite(breakdown.effectivePerUserCents)
      ? breakdown.effectivePerUserCents
      : localEffectiveCents
  ) / 100;
  const displayBands = (hasServerBreakdown && Array.isArray(breakdown.tiers) && breakdown.tiers.length)
    ? breakdown.tiers
    : localBands;
  const totalDueDollars = Number.isFinite(amountCents) && amountCents > 0
    ? (amountCents / 100)
    : localTotalDollars;

  if (checkoutMode === 'embedded') {
    return (
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ clientSecret, onComplete }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  }

  return (
    <form id="stripe-form" onSubmit={handleSubmit} style={{ width: '100%', boxSizing: 'border-box', background: '#fff', padding: '24px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: 0, textAlign: 'center' }}>Deploy Infrastructure</h3>
        
        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '6px', fontSize: '14px', marginTop: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '5px' }}>
            <span>Pre-calculated Usage Deposit:</span>
            <span style={{ whiteSpace: 'nowrap' }}>${displayEscrowDollars.toFixed(2)}</span>
          </div>
          <div style={{
            background: '#fff8e1',
            border: '1px solid #ffe0a3',
            borderRadius: '4px',
            padding: '8px 10px',
            margin: '6px 0 10px 0',
            fontSize: '12px',
            color: '#8a6d3b',
            lineHeight: '1.4'
          }}>
            <strong>Please note:</strong> The usage deposit shown above is an
            <em> estimate</em> based on your selected capacity and configuration.
            Your actual usage may be higher or lower, so the final amount applied
            against this deposit could differ from this figure.
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: '5px' }}>
            <span style={{ flex: '1', minWidth: '0' }}>Service Cost ({displayCap} users, {displayTierLabel} — graduated):</span>
            <span style={{ whiteSpace: 'nowrap' }}>${displaySetupDollars.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px', color: '#666' }}>
            <span>Effective rate:</span>
            <span>${displayEffectivePerUserDollars.toFixed(2)} / user / mo</span>
          </div>
          {displayBands && displayBands.length > 1 && (
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '5px' }}>
              {displayBands.map((b, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>&nbsp;&nbsp;{b.label}: {b.users} × ${(b.ratePerUserCents / 100).toFixed(2)}</span>
                  <span>${(b.subtotalCents / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }}/>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', fontWeight: 'bold', fontSize: '16px' }}>
            <span>Total Due:</span>
            <span style={{ whiteSpace: 'nowrap' }}>${totalDueDollars.toFixed(2)} USD</span>
          </div>
        </div>
      </div>
      <AddressElement options={{ mode: 'billing' }} />
      <PaymentElement
        onReady={() => setIsElementReady(true)}
        options={{
          fields: { billingDetails: { name: 'never', phone: 'never', address: 'never' } },
          wallets: { applePay: 'never', googlePay: 'never' }
        }}
      />
      {errorMessage && <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button 
           type="button" 
           onClick={onCancel} 
           disabled={isProcessing}
           className="btn btn-secondary" 
           style={{ flex: 1, padding: '10px' }}
        >
          Cancel
        </button>
        <button 
           type="submit" 
            disabled={!stripe || !elements || !isElementReady || isProcessing}
           className="btn btn-primary" 
           style={{ flex: 1, padding: '10px' }}
        >
          {isProcessing ? 'Processing...' : 'Pay & Deploy'}
        </button>
      </div>
    </form>
  );
};

export default StripeCheckoutForm;
